import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock3, Download, Loader2, Plus, Printer, RefreshCw, Trash2, Unplug } from "lucide-react";
import { api, bytes, dt, errMsg, exportCsv, rp } from "@/lib/api";
import { VoucherCard } from "@/components/VoucherCard";
import { cardPrice } from "@/pages/admin/PrintVouchers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const newCode = (n = 5) => Array.from({ length: n }, () => CHARS[Math.floor(Math.random() * CHARS.length)]).join("");

const Field = ({ label, ...props }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
    <Input className="h-11 rounded-xl bg-secondary/50" {...props} />
  </div>
);

export default function Hotspot() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "users";
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [settings, setSettings] = useState({});
  const [busy, setBusy] = useState(false);
  const [extend, setExtend] = useState(null);      // user hotspot yang diperpanjang
  const [extProfile, setExtProfile] = useState("");
  const [form, setForm] = useState(() => { const c = newCode(); return { username: c, password: c, profile: "", limit_uptime: "", price: 0 }; });
  const [gen, setGen] = useState({ count: 10, length: 5, prefix: "", profile: "", limit_uptime: "", price: 5000, comment: "" });
  const [batch, setBatch] = useState(null);
  const [added, setAdded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fBatch, setFBatch] = useState("");
  const [batches, setBatches] = useState([]);
  const [sel, setSel] = useState([]);
  const [qReset, setQReset] = useState("");
  const [days, setDays] = useState(30);
  const [unused, setUnused] = useState(null);
  const [scanning, setScanning] = useState(false);

  const loadAll = async () => {
    try {
      const [u, a, h, p, s, b] = await Promise.all([
        api.get(`/hotspot/users?q=${encodeURIComponent(q)}&batch=${encodeURIComponent(fBatch)}`), api.get("/hotspot/active"),
        api.get("/hotspot/history"), api.get("/hotspot/profiles"), api.get("/settings"), api.get("/hotspot/batches"),
      ]);
      setUsers(u.data); setActive(a.data); setHistory(h.data); setProfiles(p.data); setSettings(s.data); setBatches(b.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  useEffect(() => {
    const d = setTimeout(loadAll, 300);
    const t = setInterval(loadAll, 20000);
    return () => { clearTimeout(d); clearInterval(t); };
  }, [q, fBatch]);

  const bulkDelete = async (payload) => {
    const label = payload.batch ? `seluruh batch ${payload.batch}` : `${payload.usernames.length} voucher terpilih`;
    if (!window.confirm(`Hapus ${label} dari Mikrotik dan database? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      const { data } = await api.post("/hotspot/bulk-delete", { usernames: payload.usernames || [], batch: payload.batch || "" });
      toast.success(`${data.deleted} voucher dihapus`);
      setSel([]); loadAll();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const scanUnused = async () => {
    setScanning(true);
    try { const { data } = await api.get(`/hotspot/unused?days=${days}`); setUnused(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setScanning(false); }
  };

  const cleanup = async () => {
    if (!window.confirm(`Hapus ${unused?.length || 0} voucher tak terpakai (lebih dari ${days} hari) dari router? Tindakan ini permanen.`)) return;
    setScanning(true);
    try { const { data } = await api.post(`/hotspot/cleanup?days=${days}`); toast.success(`${data.deleted} voucher dibersihkan`); setUnused([]); loadAll(); }
    catch (e) { toast.error(errMsg(e)); } finally { setScanning(false); }
  };

  const addUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/hotspot/users", { ...form, price: Number(form.price) });
      toast.success(`User ${form.username} ditambahkan ke Mikrotik`);
      setAdded({ username: form.username, password: form.password, profile: form.profile, limit_uptime: form.limit_uptime, price: Number(form.price) });
      const c = newCode();
      setForm({ username: c, password: c, profile: form.profile, limit_uptime: form.limit_uptime, price: form.price });
      loadAll();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/hotspot/generate", {
        ...gen, count: Number(gen.count), length: Number(gen.length), price: Number(gen.price),
      });
      setBatch(data);
      toast.success(`${data.vouchers.length} voucher dibuat (${data.batch})`);
      loadAll();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doExtend = async () => {
    if (!extend) return;
    setBusy(true);
    try {
      const { data } = await api.post("/hotspot/extend", { username: extend.username, profile: extProfile });
      toast.success(`${data.username} diperpanjang +${data.added} (masa aktif jadi ${data.new_limit})`);
      setExtend(null);
      loadAll();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const delUser = async (u) => {
    try { await api.delete(`/hotspot/users/${encodeURIComponent(u.id)}?username=${encodeURIComponent(u.username)}`); toast.success("User dihapus"); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const kick = async (a) => {
    try { await api.delete(`/hotspot/active/${encodeURIComponent(a.id)}`); toast.success(`Koneksi ${a.user} diputus`); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const qr = qReset.trim().toLowerCase();
  const activeShown = qr
    ? active.filter((a) =>
        [a.user, a.address, a.mac_address, a.uptime].some((v) => String(v || "").toLowerCase().includes(qr))
      )
    : active;

  return (
    <>
    <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="fade-up space-y-6">
      <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl bg-secondary/60 p-1">
        {[["users", "User Hotspot"], ["add", "Add User"], ["generate", "Generate"], ["history", "History"], ["reset", "Reset"], ["bersih", "Kedaluwarsa"]].map(([v, l]) => (
          <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-lg px-4 py-2 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500/20 data-[state=active]:to-violet-600/20">{l}</TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="users">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Input
            placeholder="Cari kode / profil / batch…" value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="voucher-search" className="h-10 w-[240px] rounded-full bg-secondary/50"
          />
          <select
            className="h-10 rounded-full border border-input bg-secondary/50 px-3 text-sm" data-testid="voucher-batch-filter"
            value={fBatch} onChange={(e) => setFBatch(e.target.value)}
          >
            <option value="">— semua batch —</option>
            {batches.map((b) => <option key={b.batch} value={b.batch}>{b.batch} ({b.count})</option>)}
          </select>
          <Button size="sm" variant="outline" className="rounded-full" onClick={loadAll} data-testid="refresh-users"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Muat ulang</Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportCsv("user-hotspot.csv", users)} data-testid="export-users"><Download className="mr-1 h-3.5 w-3.5" /> CSV</Button>
          <Button size="sm" variant="outline" className="rounded-full" disabled={!sel.length && !fBatch} data-testid="print-selected"
            onClick={() => window.open(`/cetak-voucher?${sel.length ? `users=${encodeURIComponent(sel.join(","))}` : `batch=${encodeURIComponent(fBatch)}`}`, "_blank")}>
            <Printer className="mr-1 h-3.5 w-3.5" /> {sel.length ? `Cetak ${sel.length} terpilih` : fBatch ? `Cetak batch ${fBatch}` : "Cetak"}
          </Button>
          <Button
            size="sm" className="rounded-full bg-rose-600 text-white" data-testid="bulk-delete"
            disabled={!sel.length && !fBatch}
            onClick={() => bulkDelete(sel.length ? { usernames: sel } : { batch: fBatch })}
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" /> {sel.length ? `Hapus ${sel.length} terpilih` : fBatch ? `Hapus batch ${fBatch}` : "Hapus"}
          </Button>
          <p className="text-xs text-muted-foreground">{users.length} ditampilkan{loading ? " · memuat…" : ""}</p>
        </div>
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="hotspot-users-table">
            <TableHeader><TableRow>
              <TableHead className="w-10">
                <input
                  type="checkbox" data-testid="select-all-vouchers"
                  checked={!!users.length && sel.length === users.slice(0, 300).length}
                  onChange={(e) => setSel(e.target.checked ? users.slice(0, 300).map((u) => u.username) : [])}
                />
              </TableHead>
              <TableHead>Username</TableHead><TableHead>Password</TableHead><TableHead>Profile</TableHead>
              <TableHead>Batch</TableHead>
              <TableHead>Limit</TableHead><TableHead>Harga</TableHead><TableHead>Pemilik</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {users.slice(0, 300).map((u) => (
                <TableRow key={u.id} className="font-mono text-xs">
                  <TableCell data-label="setSel(e.target.checked ? users.slice(0, 300).map((u) => u.username) : [])} />">
                    <input
                      type="checkbox" data-testid={`select-voucher-${u.username}`}
                      checked={sel.includes(u.username)}
                      onChange={(e) => setSel(e.target.checked ? [...sel, u.username] : sel.filter((x) => x !== u.username))}
                    />
                  </TableCell>
                  <TableCell data-label="Username" className="font-medium">{u.username}</TableCell>
                  <TableCell data-label="Password">{u.password}</TableCell>
                  <TableCell data-label="Profile">{u.profile}</TableCell>
                  <TableCell data-label="Batch">{u.batch || "-"}</TableCell>
                  <TableCell data-label="Limit">{u.limit_uptime || "-"}</TableCell>
                  <TableCell data-label="Harga">{rp(u.price)}</TableCell>
                  <TableCell data-label="Pemilik">{u.owner}</TableCell>
                  <TableCell data-label="" className="text-right cell-actions">
                    <Button size="sm" variant="outline" data-testid={`extend-user-${u.username}`}
                      className="mr-1 h-8 rounded-full border-cyan-500/40 px-3 text-[11px] text-cyan-300"
                      onClick={() => { setExtProfile(u.profile || ""); setExtend(u); }}>
                      <Clock3 className="mr-1 h-3.5 w-3.5" /> Perpanjang
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => delUser(u)} data-testid={`delete-user-${u.username}`} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {!users.length && <TableRow><TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground cell-empty">{loading ? "Mengambil data dari Mikrotik…" : "Belum ada user hotspot"}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="add">
        <form onSubmit={addUser} className="max-w-xl space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="add-hotspot-form">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">Tambah User Hotspot</h3>
            <Button
              type="button" size="sm" variant="outline" className="rounded-full" data-testid="hs-regenerate"
              onClick={() => { const c = newCode(); setForm({ ...form, username: c, password: c }); }}
            >
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Kode Acak
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Default: username = password, 5 karakter huruf besar + angka.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Username" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toUpperCase(), password: e.target.value.toUpperCase() })} data-testid="hs-username" />
            <Field label="Password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="hs-password" />
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Profile</Label>
              <select
                className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="hs-profile"
                value={form.profile}
                onChange={(e) => {
                  const p = profiles.find((x) => x.name === e.target.value);
                  setForm({ ...form, profile: e.target.value, price: p ? p.price_user : form.price, limit_uptime: p?.limit_uptime || form.limit_uptime });
                }}
              >
                <option value="">— default —</option>
                {profiles.map((p) => <option key={p.name} value={p.name}>{p.name} {p.rate_limit ? `(${p.rate_limit})` : ""}</option>)}
              </select>
            </div>
            <Field label="Limit Uptime (mis. 1d)" value={form.limit_uptime} onChange={(e) => setForm({ ...form, limit_uptime: e.target.value })} data-testid="hs-limit" />
            <Field label="Harga Voucher" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="hs-price" />
          </div>
          <Button type="submit" disabled={busy} data-testid="hs-submit" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Simpan ke Mikrotik</>}
          </Button>
        </form>
        {added && (
          <div className="mt-6 max-w-xl rounded-2xl border border-border bg-card p-6" data-testid="added-card-panel">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-display text-lg font-semibold">Kartu Voucher</h3>
              <Button asChild size="sm" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white" data-testid="print-added">
                <Link to={`/cetak-voucher?users=${encodeURIComponent(added.username)}`} target="_blank"><Printer className="mr-1 h-3.5 w-3.5" /> Cetak Kartu</Link>
              </Button>
            </div>
            <div className="mt-5 max-w-[240px]">
              <VoucherCard v={added} wifi={settings.wifi_name} cs={settings.cs_wa} note={settings.voucher_note} loginUrl={settings.hotspot_login_url} price={cardPrice(added, settings)} />
            </div>
          </div>
        )}
      </TabsContent>

      <TabsContent value="generate">
        <div className="grid gap-6 lg:grid-cols-2">
          <form onSubmit={generate} className="space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="generate-form">
            <h3 className="font-display text-lg font-semibold">Generate Voucher Massal</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Jumlah" type="number" min="1" max="500" value={gen.count} onChange={(e) => setGen({ ...gen, count: e.target.value })} data-testid="gen-count" />
              <Field label="Panjang Karakter" type="number" min="3" max="12" value={gen.length} onChange={(e) => setGen({ ...gen, length: e.target.value })} data-testid="gen-length" />
              <Field label="Prefix" value={gen.prefix} onChange={(e) => setGen({ ...gen, prefix: e.target.value.toUpperCase() })} data-testid="gen-prefix" />
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Profile</Label>
                <select
                  className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="gen-profile"
                  value={gen.profile}
                  onChange={(e) => {
                    const p = profiles.find((x) => x.name === e.target.value);
                    setGen({ ...gen, profile: e.target.value, price: p ? p.price_user : gen.price, limit_uptime: p?.limit_uptime || gen.limit_uptime });
                  }}
                >
                  <option value="">— default —</option>
                  {profiles.map((p) => <option key={p.name} value={p.name}>{p.name} {p.rate_limit ? `(${p.rate_limit})` : ""}</option>)}
                </select>
              </div>
              <Field label="Limit Uptime" value={gen.limit_uptime} onChange={(e) => setGen({ ...gen, limit_uptime: e.target.value })} data-testid="gen-limit" />
              <Field label="Harga / Voucher" type="number" value={gen.price} onChange={(e) => setGen({ ...gen, price: e.target.value })} data-testid="gen-price" />
              <Field label="Comment (catatan di Mikrotik)" value={gen.comment} onChange={(e) => setGen({ ...gen, comment: e.target.value })} data-testid="gen-comment" />
            </div>
            <p className="text-xs text-muted-foreground">Kode dibuat otomatis: huruf besar + angka, username = password.</p>
            <Button type="submit" disabled={busy} data-testid="gen-submit" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}
            </Button>
          </form>

          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">Hasil / Kartu Voucher</h3>
              {batch && (
                <div className="flex gap-2">
                  <Button asChild size="sm" className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white" data-testid="open-print-page">
                    <Link to={`/cetak-voucher?batch=${encodeURIComponent(batch.batch)}`}><Printer className="mr-1 h-3.5 w-3.5" /> Cetak Kartu</Link>
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportCsv(`${batch.batch}.csv`, batch.vouchers)} data-testid="export-vouchers"><Download className="mr-1 h-3.5 w-3.5" /> CSV</Button>
                </div>
              )}
            </div>
            {batch ? (
              <div id="print-area" className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {batch.vouchers.map((v) => (
                  <VoucherCard key={v.username} v={v} wifi={settings.wifi_name} cs={settings.cs_wa} note={settings.voucher_note} loginUrl={settings.hotspot_login_url} price={cardPrice(v, settings)} />
                ))}
              </div>
            ) : (
              <p className="mt-6 text-sm text-muted-foreground">Belum ada batch. Isi formulir untuk membuat voucher.</p>
            )}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="history">
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="history-table">
            <TableHeader><TableRow><TableHead>Username</TableHead><TableHead>IP</TableHead><TableHead>MAC</TableHead><TableHead>Durasi</TableHead><TableHead>Login</TableHead></TableRow></TableHeader>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id} className="font-mono text-xs">
                  <TableCell data-label="Username">{h.username}</TableCell><TableCell data-label="IP">{h.ip_address}</TableCell><TableCell data-label="MAC">{h.mac_address}</TableCell>
                  <TableCell data-label="Durasi">{h.uptime}</TableCell><TableCell data-label="Login">{dt(h.login_at)}</TableCell>
                </TableRow>
              ))}
              {!history.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground cell-empty">Belum ada riwayat login</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="reset">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Input
            placeholder="Cari kode voucher / IP / MAC…" value={qReset} onChange={(e) => setQReset(e.target.value)}
            data-testid="reset-search" className="h-10 w-[260px] rounded-full bg-secondary/50"
          />
          {qReset && (
            <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => setQReset("")} data-testid="reset-search-clear">Bersihkan</Button>
          )}
          <Button size="sm" variant="outline" className="rounded-full" onClick={loadAll} data-testid="refresh-active"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Muat ulang</Button>
          <p className="text-xs text-muted-foreground" data-testid="reset-count">{activeShown.length} dari {active.length} user online</p>
        </div>
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="active-table">
            <TableHeader><TableRow><TableHead>User</TableHead><TableHead>IP</TableHead><TableHead>MAC</TableHead><TableHead>Uptime</TableHead><TableHead>Data</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {activeShown.map((a) => (
                <TableRow key={a.id} className="font-mono text-xs">
                  <TableCell data-label="User">{a.user}</TableCell><TableCell data-label="IP">{a.address}</TableCell><TableCell data-label="MAC">{a.mac_address}</TableCell>
                  <TableCell data-label="Uptime">{a.uptime}</TableCell><TableCell data-label="Data">{bytes(a.bytes_in + a.bytes_out)}</TableCell>
                  <TableCell data-label="" className="text-right cell-actions">
                    <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => kick(a)} data-testid={`kick-${a.user}`}><Unplug className="mr-1 h-3.5 w-3.5" /> Putuskan</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!activeShown.length && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground cell-empty">{loading ? "Mengambil data dari Mikrotik…" : qReset ? `Tidak ada user online yang cocok dengan "${qReset}"` : "Tidak ada user hotspot online"}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="bersih">
        <div className="max-w-3xl space-y-5 rounded-2xl border border-border bg-card p-6" data-testid="cleanup-panel">
          <div>
            <h3 className="font-display text-lg font-semibold">Bersihkan Voucher Kedaluwarsa</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Menghapus voucher yang <b>belum pernah dipakai</b> (uptime 0) dan dibuat lebih dari periode di bawah ini.
              Voucher yang sudah/sedang dipakai tidak akan tersentuh.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="h-10 rounded-full border border-input bg-secondary/50 px-3 text-sm" data-testid="cleanup-days"
              value={days} onChange={(e) => { setDays(Number(e.target.value)); setUnused(null); }}
            >
              {[30, 60, 90].map((d) => <option key={d} value={d}>lebih dari {d} hari</option>)}
            </select>
            <Button size="sm" variant="outline" className="rounded-full" onClick={scanUnused} disabled={scanning} data-testid="scan-unused">
              {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="mr-1 h-3.5 w-3.5" /> Periksa</>}
            </Button>
            <Button
              size="sm" className="rounded-full bg-rose-600 text-white" data-testid="cleanup-now"
              disabled={scanning || !unused?.length} onClick={cleanup}
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Bersihkan {unused?.length || 0} voucher
            </Button>
          </div>
          {unused && (
            <p className="text-sm" data-testid="unused-count">
              Ditemukan <b>{unused.length}</b> voucher tak terpakai lebih dari {days} hari.
            </p>
          )}
          {!!unused?.length && (
            <div className="mobile-cards max-h-[380px] overflow-auto rounded-xl border border-border scrollbar-thin">
              <Table data-testid="unused-table">
                <TableHeader><TableRow><TableHead>Username</TableHead><TableHead>Profile</TableHead><TableHead>Batch</TableHead><TableHead>Dibuat</TableHead></TableRow></TableHeader>
                <TableBody>
                  {unused.slice(0, 300).map((v) => (
                    <TableRow key={v.username} className="font-mono text-xs">
                      <TableCell data-label="Username">{v.username}</TableCell><TableCell data-label="Profile">{v.profile}</TableCell>
                      <TableCell data-label="Batch">{v.batch || "-"}</TableCell><TableCell data-label="Dibuat">{v.created_at ? dt(v.created_at) : "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </TabsContent>
    </Tabs>

      {/* perpanjang masa aktif user hotspot */}
      {extend && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm" data-testid="extend-dialog">
          <div className="w-full max-w-sm space-y-4 rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-semibold">Perpanjang masa aktif</h3>
            <div className="space-y-1 rounded-xl border border-border bg-secondary/40 p-3 font-mono text-xs">
              <p>User: {extend.username}</p>
              <p>Profile: {extend.profile || "default"}</p>
              <p>Masa aktif: {extend.limit_uptime || "tanpa batas"} · terpakai {extend.uptime || "0s"}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Paket Tambahan</Label>
              <select
                className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm" data-testid="extend-profile"
                value={extProfile} onChange={(e) => setExtProfile(e.target.value)}
              >
                <option value="">— pilih paket —</option>
                {profiles.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name}{p.limit_uptime ? ` · +${p.limit_uptime}` : " (belum ada masa aktif)"}
                  </option>
                ))}
              </select>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                Waktu ditambahkan sesuai masa aktif paket (user profile) yang dipilih. Pengguna yang sedang online
                tidak perlu login ulang atau membeli kode baru.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" className="rounded-full" onClick={() => setExtend(null)} data-testid="extend-cancel">Batal</Button>
              <Button className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white" onClick={doExtend} disabled={busy || !extProfile} data-testid="extend-confirm">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Clock3 className="mr-1.5 h-4 w-4" /> Perpanjang Sekarang</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
